<script lang="ts">
  /**
   * Filter-bar line 3: the selected category chips, one per *category*, never per family and never
   * per raw DLP name (spec 6.3 as amended by #2244). Nothing selected, nothing renders - not even
   * the line.
   *
   * A chip reads the category label alone, or `Financial ID · 3 of 7 types` when only some of the
   * category's names are selected - the stale-org-default signal, a saved default that predates a
   * change to the category's membership. `n` is the category search's own membership, so the chip
   * is partial exactly when the search shows an indeterminate tick. × removes the whole category.
   *
   * Overflow past one line becomes `+N more`. The line is *measured* here, not guessed - only the
   * component knows the rendered geometry - but that is all this file decides: it reads each
   * chip's box and hands the numbers to `chipsOnFirstLine` and `visibleChipLimit`, and the split,
   * the label and the title are `collapseChips`. All three are in the view model, where they are
   * probed. jsdom reports `offsetTop === 0` for everything, so under vitest the measurement
   * collapses to "everything fits" - which is why `maxVisible` exists, as the tests' way in.
   */
  import { tick } from "svelte";

  import { categoryColour } from "../categoryLabels";
  import { type ActiveCategoryChip, chipsOnFirstLine, collapseChips, visibleChipLimit } from "./activeInfoTypes";

  let {
    chips,
    maxVisible,
    onRemove,
  }: {
    chips: ActiveCategoryChip[];
    /**
     * Chips shown before `+N more`. Omitted, the row measures itself; the tests pass it because
     * jsdom has no layout to measure.
     */
    maxVisible?: number;
    /** Called with every selected raw name of the chip's category when its × is pressed. */
    onRemove: (rawNames: string[]) => void;
  } = $props();

  let row: HTMLElement | undefined = $state();
  let measuredLimit = $state(Number.POSITIVE_INFINITY);
  let expanded = $state(false);

  const limit = $derived(maxVisible ?? measuredLimit);
  const collapsed = $derived(collapseChips(chips, limit));
  const shown = $derived(expanded ? chips : collapsed.shown);
  const hasOverflow = $derived(collapsed.overflow.length > 0);

  $effect(() => {
    // Re-measure whenever the chip set changes.
    void chips.length;
    const node = row;
    if (!node || maxVisible !== undefined) return;

    let cancelled = false;
    void tick().then(() => {
      if (cancelled) return;
      const boxes = [...node.querySelectorAll<HTMLElement>("[data-chip]")].map((chip) => ({
        offsetTop: chip.offsetTop,
        offsetHeight: chip.offsetHeight,
      }));
      if (boxes.length === 0) return;
      const fit = chipsOnFirstLine(boxes);
      if (fit < boxes.length) measuredLimit = visibleChipLimit(fit, boxes.length);
    });
    return () => {
      cancelled = true;
    };
  });
</script>

{#if chips.length > 0}
  <div class="flex flex-wrap items-center gap-1 min-w-0" bind:this={row} data-testid="active-category-chips">
    {#each shown as chip (chip.category)}
      <span
        class="inline-flex items-center gap-1 rounded-full border border-black/10 px-2 py-0.5 text-xs font-medium text-offblack whitespace-nowrap"
        style="background:{categoryColour(chip)}"
        data-chip
        data-testid="active-category-chip"
        data-category={chip.category}
        title={chip.displayNames.join("\n")}
      >
        {chip.label}
        {#if chip.isPartial}
          <span class="text-[11px] font-normal tabular-nums" data-testid="active-category-chip-partial"
            >· {chip.summary}</span
          >
        {/if}
        <button
          type="button"
          class="rounded-full px-0.5 leading-none text-offblack/60 hover:text-red-700"
          data-testid="active-category-chip-remove"
          aria-label="Remove {chip.label}"
          onclick={() => onRemove(chip.activeInfoTypes)}>×</button
        >
      </span>
    {/each}
    {#if hasOverflow}
      <button
        type="button"
        class="text-xs text-gray-500 underline whitespace-nowrap"
        data-testid="active-category-chips-more"
        title={collapsed.overflowTitle}
        onclick={() => (expanded = !expanded)}
      >
        {expanded ? "collapse" : collapsed.moreLabel}
      </button>
    {/if}
  </div>
{/if}
